import React, { useState } from "react";
import {
    Typography,
    Box,
    Paper,
    Container,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Grid,
    Card,
    CardContent,
    Alert,
    AlertTitle,
} from "@mui/material";
import {
    ExpandMore,
    MenuBook,
    HelpOutline,
    Info,
    Verified,
    LocationOn,
    Business,
    Category,
    Scale,
    People,
    Security,
    VerifiedUser,
    Assessment,
    ContactSupport,
    Update,
    Share,
    Policy,
} from "@mui/icons-material";
import { useTranslation } from 'react-i18next';

const KeyTermsFAQ = () => {
    const { t } = useTranslation();
    const [expandedTerm, setExpandedTerm] = useState(false);
    const [expandedFAQ, setExpandedFAQ] = useState(false);

    const handleTermChange = (panel) => (event, isExpanded) => {
        setExpandedTerm(isExpanded ? panel : false);
    };

    const handleFAQChange = (panel) => (event, isExpanded) => {
        setExpandedFAQ(isExpanded ? panel : false);
    };

    const keyTerms = [
        {
            id: 'in-kind-aid',
            term: t('In-Kind Aid'),
            definition: t('in_kind_aid_definition'),
            icon: <Category sx={{ color: '#0c9fc1' }} />
        },
        {
            id: 'receiving-entity',
            term: t('Receiving Entity'),
            definition: t('receiving_entity_definition'),
            icon: <Business sx={{ color: '#0c9fc1' }} />
        },
        {
            id: 'distributing-entity',
            term: t('Distributing Entity'),
            definition: t('distributing_entity_definition'),
            icon: <LocationOn sx={{ color: '#0c9fc1' }} />
        },
        {
            id: 'aid-categories',
            term: t('Aid Categories'),
            definition: t('aid_categories_definition'),
            icon: <MenuBook sx={{ color: '#0c9fc1' }} />
        },
        {
            id: 'unit-discrepancy',
            term: t('Unit of Measurement Discrepancy'),
            definition: t('unit_discrepancy_definition'),
            icon: <Scale sx={{ color: '#0c9fc1' }} />
        },
        {
            id: 'displaced-persons',
            term: t('Displaced Persons'),
            definition: t('displaced_persons_definition'),
            icon: <People sx={{ color: '#0c9fc1' }} />
        }
    ];

    const faqs = [
        {
            id: 'data-verification',
            question: t('How does Gherbal verify data?'),
            answer: t('data_verification_answer'),
            icon: <Verified sx={{ color: '#38a169' }} />
        },
        {
            id: 'missing-data',
            question: t('Why is some data missing or incomplete?'),
            answer: t('missing_data_answer'),
            icon: <Info sx={{ color: '#f6ad55' }} />
        },
        {
            id: 'regional-gaps',
            question: t('Why is data missing for region X?'),
            answer: t('regional_gaps_answer'),
            icon: <LocationOn sx={{ color: '#0c9fc1' }} />
        },
        {
            id: 'data-accuracy',
            question: t('How accurate is this data?'),
            answer: t('data_accuracy_answer'),
            icon: <Assessment sx={{ color: '#0c9fc1' }} />
        },
        {
            id: 'real-time-updates',
            question: t('Is this data updated in real-time?'),
            answer: t('real_time_updates_answer'),
            icon: <Update sx={{ color: '#0d7377' }} />
        },
        {
            id: 'data-usage',
            question: t('Who can use this data?'),
            answer: t('data_usage_answer'),
            icon: <Share sx={{ color: '#0c9fc1' }} />
        },
        {
            id: 'government-focus',
            question: t('Why focus on government-managed aid?'),
            answer: t('government_focus_answer'),
            icon: <Policy sx={{ color: '#0c9fc1' }} />
        }
    ];

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            {/* Header Section */}
            <Box sx={{ mb: 6, textAlign: 'center', maxWidth: '1200px', mx: 'auto' }}>
                <Typography
                    variant="h2"
                    sx={{
                        fontWeight: 700,
                        mb: 2,
                        background: 'linear-gradient(135deg, #0c9fc1 0%, #4db6d1 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                    }}
                >
                    {t('Key Terms & FAQs')}
                </Typography>

                <Typography
                    variant="h6"
                    sx={{
                        textAlign: 'center',
                        color: 'text.secondary',
                        maxWidth: '800px',
                        mx: 'auto',
                        mb: 3,
                        lineHeight: 1.6
                    }}
                >
                    {t('faq_subtitle')}
                </Typography>

                {/* Overview Alert */}
                <Alert
                    severity="info"
                    sx={{
                        mb: 4,
                        borderRadius: 3,
                        textAlign: 'left',
                        maxWidth: '1000px',
                        mx: 'auto',
                        backgroundColor: 'rgba(12, 159, 193, 0.08)',
                        border: '1px solid rgba(12, 159, 193, 0.2)',
                        '& .MuiAlert-icon': {
                            color: '#0c9fc1'
                        }
                    }}
                >
                    <AlertTitle sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#0c9fc1' }}>
                        {/* <Info sx={{ mr: 1, verticalAlign: 'middle' }} /> */}
                        {t('About This Information')}
                    </AlertTitle>
                    <Typography variant="body1" sx={{ color: 'text.primary' }}>
                        {t('faq_overview_text')}
                    </Typography>
                </Alert>
            </Box>

            <Box sx={{ maxWidth: '1200px', mx: 'auto' }}>
                <Grid container spacing={4}>
                    {/* Key Terms Section */}

                    <Grid item xs={12}>
                        <Paper elevation={2} sx={{
                            p: 4,
                            borderRadius: 4,
                            minWidth: "100%",
                            height: '100%',
                            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                            border: '1px solid rgba(12, 159, 193, 0.1)'
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <MenuBook sx={{ fontSize: 32, mr: 2, color: '#0c9fc1' }} />
                                <Typography variant="h4" sx={{
                                    fontWeight: 600,
                                    color: '#0c9fc1',
                                    fontSize: { xs: '1.5rem', md: '2rem' }
                                }}>
                                    {t('Key Terms and Definitions')}
                                </Typography>
                            </Box>

                            <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', lineHeight: 1.6 }}>
                                {t('key_terms_intro')}
                            </Typography>

                            {keyTerms.map((term) => (
                                <Accordion
                                    key={term.id}
                                    expanded={expandedTerm === term.id}
                                    onChange={handleTermChange(term.id)}
                                    sx={{
                                        mb: 2,
                                        borderRadius: 2,
                                        '&:before': { display: 'none' },
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMore sx={{ color: 'white' }} />}
                                        sx={{
                                            background: 'linear-gradient(135deg, #0c9fc1 0%, #4db6d1 100%)',
                                            color: 'white',
                                            borderRadius: '8px 8px 0 0',
                                            '&.Mui-expanded': {
                                                borderRadius: '8px 8px 0 0'
                                            },
                                            '&:hover': {
                                                background: 'linear-gradient(135deg, #0a7a96 0%, #0c9fc1 100%)'
                                            }
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                            {term.icon}
                                            <Typography variant="h6" sx={{ fontWeight: 600, ml: 2 }}>
                                                {term.term}
                                            </Typography>
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ p: 3 }}>
                                        <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                                            {term.definition}
                                        </Typography>
                                    </AccordionDetails>
                                </Accordion>
                            ))}
                        </Paper>
                    </Grid>

                    {/* FAQs Section */}
                    <Grid item xs={12}>
                        <Paper elevation={2} sx={{
                            p: 4,
                            borderRadius: 4,
                            minWidth: "100%",
                            height: '100%',
                            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                            border: '1px solid rgba(12, 159, 193, 0.1)'
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <HelpOutline sx={{ fontSize: 32, mr: 2, color: '#0d7377' }} />
                                <Typography variant="h4" sx={{
                                    fontWeight: 600,
                                    color: '#0d7377',
                                    fontSize: { xs: '1.5rem', md: '2rem' }
                                }}>
                                    {t('Frequently Asked Questions')}
                                </Typography>
                            </Box>

                            <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', lineHeight: 1.6 }}>
                                {t('faq_intro')}
                            </Typography>

                            {faqs.map((faq) => (
                                <Accordion
                                    key={faq.id}
                                    expanded={expandedFAQ === faq.id}
                                    onChange={handleFAQChange(faq.id)}
                                    sx={{
                                        mb: 2,
                                        borderRadius: 2,
                                        '&:before': { display: 'none' },
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMore sx={{ color: 'white' }} />}
                                        sx={{
                                            background: 'linear-gradient(135deg, #0d7377 0%, #14a085 100%)',
                                            color: 'white',
                                            borderRadius: '8px 8px 0 0',
                                            '&.Mui-expanded': {
                                                borderRadius: '8px 8px 0 0'
                                            },
                                            '&:hover': {
                                                background: 'linear-gradient(135deg, #0a5d61 0%, #0d7377 100%)'
                                            }
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                            {faq.icon}
                                            <Typography variant="h6" sx={{
                                                fontWeight: 600,
                                                ml: 2,
                                                fontSize: { xs: '0.9rem', md: '1rem' },
                                                lineHeight: 1.3
                                            }}>
                                                {faq.question}
                                            </Typography>
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ p: 3 }}>
                                        <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                                            {faq.answer}
                                        </Typography>
                                    </AccordionDetails>
                                </Accordion>
                            ))}
                        </Paper>
                    </Grid>
                </Grid>
            </Box>

            {/* Additional Information Section */}
            <Box sx={{ maxWidth: '1200px', mx: 'auto', mt: 4 }}>
                <Paper elevation={1} sx={{
                    p: 4,
                    borderRadius: 4,
                    background: 'linear-gradient(135deg, rgba(12, 159, 193, 0.03) 0%, rgba(77, 182, 209, 0.03) 100%)',
                    border: '1px solid rgba(12, 159, 193, 0.1)',
                }}>


                  
                    <Typography variant="h5" sx={{
                        fontWeight: 600,
                        mb: 3,
                        color: '#0c9fc1',
                        textAlign: 'center',
                        fontSize: { xs: '1.3rem', md: '1.5rem' }
                    }}>
                        {t('Data Sources and Methodology')}
                    </Typography>

                    <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                            <Card sx={{
                                height: '100%',
                                borderRadius: 3,
                                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                                border: '1px solid rgba(12, 159, 193, 0.1)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 8px 25px rgba(12, 159, 193, 0.15)'
                                }
                            }}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <Security sx={{ color: '#38a169', mr: 1 }} />
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            {t('Official Sources')}
                                        </Typography>
                                    </Box>
                                    <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                                        {t('official_sources_text')}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Card sx={{
                                height: '100%',
                                borderRadius: 3,
                                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                                border: '1px solid rgba(12, 159, 193, 0.1)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 8px 25px rgba(12, 159, 193, 0.15)'
                                }
                            }}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <VerifiedUser sx={{ color: '#0c9fc1', mr: 1 }} />
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            {t('Transparency Commitment')}
                                        </Typography>
                                    </Box>
                                    <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                                        {t('transparency_commitment_text')}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Card sx={{
                                height: '100%',
                                borderRadius: 3,
                                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                                border: '1px solid rgba(12, 159, 193, 0.1)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 8px 25px rgba(12, 159, 193, 0.15)'
                                }
                            }}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <ContactSupport sx={{ color: '#f6ad55', mr: 1 }} />
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            {t('Contact Information')}
                                        </Typography>
                                    </Box>
                                    <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                                        {t('contact_information_text')}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Paper>
            </Box>
        </Container>
    );
};

export default KeyTermsFAQ;
